import * as React from "react";
import {
	type ColumnDef,
	type ColumnFiltersState,
	type VisibilityState,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { Button } from "../../../components/ui/button";
import { DataTableComponents } from "../../../components/tables/table.components";
import Spinner from "../../../components/spinner.components";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../../hooks/use-toast";
import { handleHttpError } from "../../../utils/utils";

import { DateTime } from "luxon";
import { useGetCsvLogAdminHttp } from "../../../hooks/http/admin/favorites/get-csv-log.admin.http";
import type { ICSVUpload } from "../../../hooks/http/admin/favorites/add-new-csv-favorite-collection.admin.http";
import { RetryAll } from "../../../components/action-buttons/favorites/retry-failed-button.components";

export const FavoriteCsvTableAdminPartial: React.FunctionComponent = () => {
	const { favoriteId } = useParams();
	const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
		[],
	);
	const [columnVisibility, setColumnVisibility] =
		React.useState<VisibilityState>({});
	const [rowSelection, setRowSelection] = React.useState({});
	const [data, setData] = React.useState<ICSVUpload[]>([]);
	const navigate = useNavigate();

	const { toast } = useToast();

	const {
		execute: getCsvLogs,
		fetching: fetchingCsvLog,
		cancel: cancelGetCsvLog,
	} = useGetCsvLogAdminHttp();

	async function getCsv() {
		cancelGetCsvLog();
		if (!favoriteId) {
			return;
		}
		const response = await getCsvLogs({
			body: null,
			pathParams: { favouriteId: favoriteId },
		});

		if (!response.success) {
			return handleHttpError(response, toast);
		}

		setData(response.data.csvLogs);
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	React.useEffect(() => {
		setData([]);
		getCsv();
	}, []);

	function handleViewDetails(id: string) {
		navigate(`/admin/favorites/${favoriteId}/csv/${id}`);
	}

	const RenderActionButtons = (csv: ICSVUpload) => {
		return (
			<div className="flex justify-end items-center gap-4">
				<RetryAll csv={csv} />
				<Button
					onClick={() => {
						handleViewDetails(csv.id);
					}}
					className="bg-tertiarybuttonbg p-2.5 border border-border rounded-md font-semibold tracking-wider"
				>
					View Details
				</Button>
			</div>
		);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const columns = React.useMemo<ColumnDef<ICSVUpload>[]>(
		() => [
			{
				accessorKey: "title",
				header: "File name",
				cell: ({ row }) => {
					const { title } = row.original;
					return (
						<div className="flex items-center gap-3 w-40 font-semibold tracking-wider">
							{title}
						</div>
					);
				},
			},
			{
				accessorKey: "createdAt",
				header: "Date",
				cell: ({ row }) => {
					const { createdAt } = row.original;

					return (
						<div className="flex flex-col justify-start items-start w-32 font-semibold">
							<p className="text-base">
								{DateTime.fromISO(createdAt.toString()).toFormat("dd-MM-yyyy")}
							</p>
							<p className="text-secondary text-sm">
								{DateTime.fromISO(createdAt.toString()).toFormat("hh:mm a")}
							</p>
						</div>
					);
				},
			},
			{
				accessorKey: "createdBy",
				header: "Uploaded by",
				cell: ({ row }) => {
					const { createdBy } = row.original;
					return (
						<div className="flex items-center w-32 font-semibold">
							{createdBy.name}
						</div>
					);
				},
			},
			{
				accessorKey: "failedCount",
				header: "Errors",
				cell: ({ row }) => {
					const { failedCount } = row.original;

					return (
						<div className="flex items-center gap-2 font-semibold text-rejectbuttontext capitalize tracking-wider">
							{failedCount} uploads
						</div>
					);
				},
			},

			{
				accessorKey: "id",
				header: "",
				cell: ({ row: social }) => RenderActionButtons(social.original),
			},
		],
		[],
	);
	const table = useReactTable({
		data,
		columns,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		onRowSelectionChange: setRowSelection,
		state: {
			columnFilters,
			columnVisibility,
			rowSelection,
		},
		manualPagination: true,
	});

	if (fetchingCsvLog) {
		return (
			<div className="flex justify-center items-center pt-20 w-full">
				<Spinner />
			</div>
		);
	}

	return (
		<div className="">
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
			<div
				id="getSocials"
				onClick={() => {
					getCsv();
				}}
				className="hidden"
			/>
			<DataTableComponents
				table={table as ReturnType<typeof useReactTable>}
				columns={columns as ColumnDef<ICSVUpload>[]}
				setPageNo={() => {}}
				loading={fetchingCsvLog || false}
				tableType="csv"
			/>
		</div>
	);
};
